import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, ShieldAlert, Loader, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/auth/admin/segundo-factor/`;

// El color del texto va declarado a proposito. Sin el, el input hereda el de
// su contenedor —un gris muy claro— y lo que se escribe queda casi del color
// del fondo: el codigo estaba ahi, pero no se veia.
const INPUT = 'campo';

/**
 * Activación y baja del segundo factor de la cuenta de plataforma.
 *
 * El alta tiene dos pasos a propósito: primero se muestra el código para
 * cargarlo en la aplicación del teléfono, y recién cuando la persona escribe un
 * código correcto el factor pasa a ser obligatorio. Activarlo de una sola vez
 * dejaría la cuenta fuera de alcance si la aplicación hubiera quedado mal
 * configurada, y eso no se descubriría hasta el siguiente ingreso.
 */
// Este bloque no usa <form>. Vive dentro del formulario de la página que lo
// aloja, y HTML no admite formularios anidados: el analizador del navegador
// descarta la etiqueta interna, de modo que su onSubmit nunca llega a asociarse
// y su botón de envío termina enviando el formulario de afuera. El síntoma es
// desconcertante —el botón "parece" no hacer nada— y no deja rastro en el
// servidor, porque la petición nunca sale.
const alPresionarEnter = (accion) => (e) => {
    if (e.key === 'Enter') {
        e.preventDefault();
        accion();
    }
};

export default function SegundoFactor() {
    const [estado, setEstado] = useState(null);
    const [alta, setAlta] = useState(null);          // { secreto, uri }
    const [codigo, setCodigo] = useState('');
    const [password, setPassword] = useState('');
    const [respaldos, setRespaldos] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [trabajando, setTrabajando] = useState(false);
    const [error, setError] = useState(null);
    const [copiado, setCopiado] = useState(false);

    const consultar = async () => {
        try {
            const { data } = await axios.get(API);
            setEstado(data);
        } catch {
            setError('No se pudo consultar el estado del segundo factor.');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { consultar(); }, []);

    const iniciar = async () => {
        setTrabajando(true); setError(null);
        try {
            const { data } = await axios.post(API, {});
            setAlta(data);
            setCodigo('');
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo iniciar la configuración.');
        } finally {
            setTrabajando(false);
        }
    };

    const confirmar = async () => {
        setTrabajando(true); setError(null);
        try {
            await axios.put(API, { codigo });
            setAlta(null);
            setCodigo('');
            await consultar();
        } catch (e2) {
            setError(e2.response?.data?.error || 'No se pudo confirmar el código.');
        } finally {
            setTrabajando(false);
        }
    };

    const desactivar = async () => {
        if (!window.confirm('¿Desactivar el segundo factor? La cuenta vuelve a entrar solo con la contraseña.')) return;
        setTrabajando(true); setError(null);
        try {
            await axios.delete(API, { data: { password, codigo } });
            setPassword(''); setCodigo(''); setRespaldos(null);
            await consultar();
        } catch (e2) {
            setError(e2.response?.data?.error || 'No se pudo desactivar.');
        } finally {
            setTrabajando(false);
        }
    };

    const reponerRespaldos = async () => {
        const codigo = window.prompt(
            'Para generar códigos nuevos, escribí el código de 6 dígitos que muestra '
            + 'tu aplicación de autenticación. Los códigos anteriores van a dejar de servir.'
        );
        if (!codigo) return;
        setTrabajando(true); setError(null);
        try {
            const { data } = await axios.post(`${API}respaldos/`, { codigo: codigo.trim() });
            setRespaldos(data.respaldos);
            await consultar();
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudieron generar códigos nuevos.');
        } finally {
            setTrabajando(false);
        }
    };

    const olvidarEquipos = async () => {
        if (!window.confirm('¿Olvidar todos los equipos? El código se va a pedir de nuevo en cada uno.')) return;
        setTrabajando(true); setError(null);
        try {
            await axios.delete(`${API}equipos/`);
            // Este equipo tambien deja de estar recordado: seguir enviando el
            // valor guardado solo lograria que el servidor lo rechace.
            localStorage.removeItem('equipo_confiable');
            await consultar();
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudieron olvidar los equipos.');
        } finally {
            setTrabajando(false);
        }
    };

    const copiarRespaldos = () => {
        navigator.clipboard?.writeText(respaldos.join('\n'))
            .then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 2000); })
            .catch(() => {});
    };

    if (cargando) {
        return (
            <div className="flex items-center gap-2 text-slate-400 text-sm font-bold">
                <Loader size={16} className="animate-spin" /> Cargando...
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {error && (
                <div className="aviso-error">
                    {error}
                </div>
            )}

            {/* Los códigos de respaldo se muestran una sola vez */}
            {respaldos && (
                <div className="bg-alerta-50 border border-alerta-200 rounded-2xl p-5">
                    <p className="text-sm font-black text-alerta-800 mb-1">Guardá estos códigos de respaldo</p>
                    <p className="text-xs text-alerta-700 mb-4">
                        Son tu única forma de entrar si perdés el teléfono. Cada uno sirve
                        una vez y no se vuelven a mostrar: anotalos en un lugar seguro.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {respaldos.map(c => (
                            <code key={c} className="bg-white border border-alerta-200 rounded-xl px-3 py-2 text-sm font-mono font-bold text-center text-slate-700">
                                {c}
                            </code>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={copiarRespaldos}
                        className="inline-flex items-center gap-2 text-xs font-black text-alerta-800 hover:text-alerta-900"
                    >
                        {copiado ? <Check size={14} /> : <Copy size={14} />}
                        {copiado ? 'Copiados' : 'Copiar los códigos'}
                    </button>
                </div>
            )}

            {/* Estado actual */}
            <div className="flex items-start gap-3">
                {estado?.activo
                    ? <ShieldCheck size={20} className="text-exito-500 mt-0.5 shrink-0" />
                    : <ShieldAlert size={20} className="text-slate-400 mt-0.5 shrink-0" />}
                <div className="text-sm">
                    <p className="font-black text-slate-700">
                        {estado?.activo ? 'Activo' : 'Desactivado'}
                    </p>
                    <p className="text-slate-500 leading-relaxed">
                        {estado?.activo
                            ? 'Al entrar desde un equipo nuevo se pide el código de tu aplicación. En los equipos que marcaste como tuyos no se vuelve a pedir.'
                            : 'Esta cuenta puede entrar al panel de cualquier negocio: si la contraseña se filtra, alcanza para llegar a los datos de todos.'}
                    </p>
                </div>
            </div>

            {/* Alta: paso 1, mostrar el código para la aplicación */}
            {!estado?.activo && !alta && (
                <button
                    type="button"
                    onClick={iniciar}
                    disabled={trabajando}
                    className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-negro-950 px-5 py-3 rounded-2xl font-black text-sm transition-colors"
                >
                    {trabajando ? 'Generando...' : 'Activar segundo factor'}
                </button>
            )}

            {alta && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                    <p className="text-sm font-black text-slate-700">
                        1. Escaneá el código con tu aplicación de autenticación
                    </p>
                    <div className="flex justify-center bg-white p-4 rounded-2xl border border-slate-200">
                        <QRCodeSVG value={alta.uri} size={168} />
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                        ¿No podés escanear? Cargalo a mano:{' '}
                        <code className="font-mono font-bold text-slate-700 break-all">{alta.secreto}</code>
                    </p>
                    <p className="text-xs text-alerta-700 bg-alerta-50 border border-alerta-200 rounded-xl px-3 py-2">
                        Si ya escaneaste antes y te quedaron varias entradas
                        <strong> SoftecPY-POS: admin</strong> en la aplicación, borralas todas
                        y escaneá solo este código: el anterior ya no sirve.
                    </p>

                    <p className="text-sm font-black text-slate-700 pt-2">
                        2. Escribí el código que muestra la aplicación
                    </p>
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        className="campo-codigo"
                        placeholder="000000"
                        value={codigo}
                        onChange={e => setCodigo(e.target.value)}
                        onKeyDown={alPresionarEnter(confirmar)}
                    />
                    <p className="text-xs text-slate-400">
                        Hasta que no confirmes, la cuenta sigue entrando solo con la contraseña.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={confirmar}
                            disabled={trabajando || codigo.trim().length < 6}
                            className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-negro-950 px-5 py-3 rounded-2xl font-black text-sm transition-colors"
                        >
                            {trabajando ? 'Confirmando...' : 'Confirmar y activar'}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setAlta(null); setError(null); }}
                            className="px-5 py-3 rounded-2xl font-black text-sm text-slate-500 hover:bg-slate-100 transition-colors"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* Códigos de respaldo: opcionales, para quien quiera una red extra */}
            {estado?.activo && (
                <details className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4">
                    <summary className="titulo-seccion cursor-pointer select-none">
                        Códigos de respaldo (opcional)
                        {estado.respaldos_disponibles > 0 && (
                            <span className="ml-2 normal-case tracking-normal text-slate-500">
                                — tenés {estado.respaldos_disponibles} sin usar
                            </span>
                        )}
                    </summary>
                    <div className="space-y-2 mt-3">
                        <p className="text-xs text-slate-500 leading-relaxed">
                            No hacen falta para el uso normal: el código sale siempre de la
                            aplicación del teléfono. Sirven solo si te quedás sin el teléfono
                            y necesitás entrar desde la web igual. Si no los generás y perdés
                            el teléfono, el segundo factor se desactiva desde el servidor con{' '}
                            <code className="font-mono text-slate-600">manage.py segundo_factor --desactivar</code>.
                        </p>
                        <button
                            type="button"
                            onClick={reponerRespaldos}
                            disabled={trabajando}
                            className="text-xs font-black text-slate-500 hover:text-slate-800 underline underline-offset-2 disabled:opacity-50"
                        >
                            {estado.respaldos_disponibles > 0 ? 'Generar códigos nuevos' : 'Generar códigos de respaldo'}
                        </button>
                    </div>
                </details>
            )}

            {/* Equipos donde no se vuelve a pedir el código */}
            {estado?.activo && estado.equipos?.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                    <p className="titulo-seccion">
                        Equipos recordados
                    </p>
                    <ul className="space-y-1.5">
                        {estado.equipos.map(eq => (
                            <li key={eq.id} className="flex items-center justify-between text-sm">
                                <span className="font-bold text-slate-700">{eq.etiqueta || 'Equipo'}</span>
                                <span className="text-xs text-slate-400">
                                    hasta el {new Date(eq.expira).toLocaleDateString('es-PY')}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-xs text-slate-500 leading-relaxed">
                        En estos equipos no se pide el código. Si alguno dejó de ser tuyo
                        —prestado, vendido, perdido— olvidalos: vuelve a pedirse en todos.
                    </p>
                    <button
                        type="button"
                        onClick={olvidarEquipos}
                        disabled={trabajando}
                        className="text-xs font-black text-slate-500 hover:text-slate-800 underline underline-offset-2 disabled:opacity-50"
                    >
                        Olvidar todos los equipos
                    </button>
                </div>
            )}

            {/* Baja: exige contraseña y código */}
            {estado?.activo && (
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                    <p className="titulo-seccion">
                        Desactivar
                    </p>
                    <p className="text-xs text-slate-500">
                        Se piden la contraseña y un código vigente: con la sesión abierta
                        alcanzaría, y entonces quien llegue a un equipo desatendido podría
                        quitarlo y quedarse con la cuenta.
                    </p>
                    <input
                        type="password"
                        autoComplete="current-password"
                        className={INPUT}
                        placeholder="Tu contraseña"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyDown={alPresionarEnter(desactivar)}
                    />
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={14}
                        className="campo-codigo text-base"
                        placeholder="Código o código de respaldo"
                        value={codigo}
                        onChange={e => setCodigo(e.target.value)}
                        onKeyDown={alPresionarEnter(desactivar)}
                    />
                    <button
                        type="button"
                        onClick={desactivar}
                        disabled={trabajando || !password || !codigo}
                        className="bg-error-50 hover:bg-error-100 disabled:opacity-50 text-error-600 border border-error-200 px-5 py-3 rounded-2xl font-black text-sm transition-colors"
                    >
                        {trabajando ? 'Desactivando...' : 'Desactivar segundo factor'}
                    </button>
                </div>
            )}
        </div>
    );
}
